import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, CalendarCheck, Inbox, Map, Settings, LogOut, ExternalLink, Menu, X, Package, Star, BookOpen, Users, ScrollText, UserCog } from "lucide-react";
import { adminApi } from "@/lib/api";

export const STATUS_LABELS = { pending: "In attesa", confirmed: "Confermata", cancelled: "Annullata", completed: "Completata" };
export const ROLE_LABELS = { owner: "Titolare", manager: "Responsabile", staff: "Operatore" };
const PILL = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-800 border-rose-200",
  completed: "bg-blue-50 text-blue-800 border-blue-200",
};
export const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${PILL[status] || ""}`} data-testid={`status-pill-${status}`}>{STATUS_LABELS[status] || status}</span>
);

export const AdminCard = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-sand/70 shadow-[0_6px_20px_rgba(34,44,37,0.05)] ${className}`}>{children}</div>
);

export const PageTitle = ({ title, sub, right }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
    <div>
      <h1 className="font-serif text-3xl text-ocean">{title}</h1>
      {sub && <p className="text-sm text-ocean/55 mt-1">{sub}</p>}
    </div>
    {right}
  </div>
);

/* roles: which roles can see the entry (server enforces the real permissions) */
const ALL = ["owner", "manager", "staff"];
const links = [
  { to: "/admin", end: true, label: "Panoramica", icon: LayoutDashboard, id: "overview", roles: ALL },
  { to: "/admin/bookings", label: "Prenotazioni", icon: CalendarCheck, id: "bookings", roles: ALL },
  { to: "/admin/inquiries", label: "Richieste", icon: Inbox, id: "inquiries", roles: ALL },
  { to: "/admin/customers", label: "Clienti", icon: Users, id: "customers", roles: ALL },
  { to: "/admin/tours", label: "Tour e disponibilità", icon: Map, id: "tours", roles: ["owner", "manager"] },
  { to: "/admin/packages", label: "Pacchetti", icon: Package, id: "packages", roles: ["owner", "manager"] },
  { to: "/admin/diary", label: "Diario di viaggio", icon: BookOpen, id: "diary", roles: ["owner", "manager"] },
  { to: "/admin/reviews", label: "Recensioni", icon: Star, id: "reviews", roles: ["owner", "manager"] },
  { to: "/admin/users", label: "Utenti e permessi", icon: UserCog, id: "users", roles: ["owner"] },
  { to: "/admin/audit", label: "Registro attività", icon: ScrollText, id: "audit", roles: ["owner"] },
  { to: "/admin/settings", label: "Impostazioni", icon: Settings, id: "settings", roles: ["owner", "manager"] },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [state, setState] = useState(localStorage.getItem("ams_admin_token") ? "checking" : "out");
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (state !== "checking") return;
    adminApi.get("/admin/me").then((r) => { setMe(r.data); setState("in"); }).catch(() => setState("out"));
  }, [state]);

  if (state === "out") return <Navigate to="/admin/login" replace />;
  if (state === "checking") return <div className="min-h-screen flex items-center justify-center bg-ivory"><div className="w-10 h-10 border-2 border-sea border-t-transparent rounded-full animate-spin" /></div>;

  const role = me?.role || "owner";
  const logout = () => { localStorage.removeItem("ams_admin_token"); navigate("/admin/login"); };

  const Nav = (
    <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
      {links.filter((l) => l.roles.includes(role)).map(({ to, end, label, icon: Icon, id }) => (
        <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} data-testid={`admin-nav-${id}`}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm ${isActive ? "bg-ivory/10 text-sand-brand" : "text-ivory/70 hover:text-ivory hover:bg-ivory/5"}`}>
          <Icon size={17} /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-ivory flex" data-testid="admin-layout">
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 bg-ocean text-ivory flex flex-col ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} style={{ transition: "transform .25s ease" }}>
        <div className="h-20 px-6 flex items-center justify-between border-b border-ivory/10">
          <Link to="/admin" aria-label="AMS Travel admin"><img src="/brand/ams-logo-light.webp" alt="AMS Travel" className="h-10 w-auto" /></Link>
          <button className="lg:hidden text-ivory p-2" onClick={() => setOpen(false)} aria-label="Chiudi"><X size={20} /></button>
        </div>
        {Nav}
        <div className="p-4 border-t border-ivory/10 space-y-1">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ivory/70 hover:text-ivory" data-testid="admin-view-site"><ExternalLink size={16} /> Vedi il sito</Link>
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-ivory/70 hover:text-ivory" data-testid="admin-logout"><LogOut size={16} /> Esci</button>
          <p className="px-4 pt-2 text-[11px] text-ivory/40 truncate" data-testid="admin-me">{me?.email} · {ROLE_LABELS[role] || role}</p>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-ocean/40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden h-16 px-4 flex items-center justify-between bg-white border-b border-sand">
          <button onClick={() => setOpen(true)} aria-label="Menu" className="p-2 -ml-2" data-testid="admin-mobile-menu"><Menu size={22} className="text-ocean" /></button>
          <img src="/brand/ams-logo-dark.webp" alt="AMS Travel" className="h-8 w-auto" />
          <span className="w-6" />
        </div>
        <main className="p-4 sm:p-6 md:p-10 max-w-7xl"><Outlet context={{ me }} /></main>
      </div>
    </div>
  );
}
